import { userApi } from '../../api';
import { useCurrentAccountId, useCurrentUser } from '../../redux/selector';
import { DETAIL, LIST, profileQueryKey } from '../../api/query';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { Account, CreateAccountRequest } from '@quick/client';
import { accountOnboardingApi, accountUserApi } from '../../api/vendorlink';
import { VENDORLINK_ACCOUNTS, VENDORLINK_NAMESPACE } from '../../api/vendorlink/query';

const SUMMARY_KEY = 'SUMMARY';
const FULL_KEY = 'FULL';

const SHORT_STALE_TIME = 1000 * 5;

export enum AccountVisibility {
  Public = 'Public',
  Private = 'Private',
}

const accountSummaryKeys = {
  all: () => [VENDORLINK_NAMESPACE, VENDORLINK_ACCOUNTS, SUMMARY_KEY],
  lists: () => [...accountSummaryKeys.all(), LIST],
  list: (visibility: AccountVisibility, search: string = '') => [...accountSummaryKeys.lists(), visibility, search],
  details: () => [...accountSummaryKeys.all(), DETAIL],
  detail: (accountId: number) => [...accountSummaryKeys.details(), accountId],
};

const joinedAccountKeys = {
  all: () => [VENDORLINK_NAMESPACE, VENDORLINK_ACCOUNTS, FULL_KEY],
  lists: () => [...joinedAccountKeys.all(), LIST],
  list: (userId?: number) => [...joinedAccountKeys.lists(), userId],
};

export const usePublicAccountSummariesQuery = (search: string | undefined = undefined) => {
  return useInfiniteQuery(
    accountSummaryKeys.list(AccountVisibility.Public, search),
    ({ pageParam }) =>
      accountOnboardingApi.listAccountSummaries({
        name: search,
        page: pageParam ?? 0,
      }),
    {
      getNextPageParam: (lastPage) => (lastPage.number < lastPage.totalPages ? lastPage.number + 1 : undefined),
      keepPreviousData: true,
    }
  );
};

export const usePrivateAccountSummariesQuery = (
  accountId: number | undefined,
  search: string | undefined = undefined
) => {
  return useInfiniteQuery(
    accountSummaryKeys.list(AccountVisibility.Private, search),
    ({ pageParam }) =>
      accountUserApi.listPrivateAccounts({
        accountId: accountId!,
        name: search,
        page: pageParam ?? 0,
      }),
    {
      getNextPageParam: (lastPage) => (lastPage.number < lastPage.totalPages ? lastPage.number + 1 : undefined),
      keepPreviousData: true,
      enabled: !!accountId,
    }
  );
};

export const useAccountSummariesQuery = (
  accountVisibility: AccountVisibility,
  search: string | undefined = undefined
) => {
  const publicResults = usePublicAccountSummariesQuery(search);
  const accountId = useCurrentAccountId();
  const privateResults = usePrivateAccountSummariesQuery(accountId, search);
  if (accountVisibility === AccountVisibility.Private) {
    return privateResults;
  }
  return publicResults;
};

export const useAccountSummaryQuery = (accountId: number | undefined) => {
  return useQuery(
    accountSummaryKeys.detail(accountId!),
    () =>
      accountOnboardingApi.getAccountSummary({
        accountId: accountId!,
      }),
    {
      keepPreviousData: true,
      enabled: !!accountId,
    }
  );
};

export const useJoinedAccountsQuery = () => {
  const user = useCurrentUser();
  return useQuery(
    joinedAccountKeys.list(user?.userId),
    async () => {
      if (user?.userId) {
        return userApi.listAccounts();
      }
      return [];
    },
    {
      staleTime: SHORT_STALE_TIME,
      keepPreviousData: true,
    }
  );
};

export const useAccountMutation = () => {
  const queryClient = useQueryClient();

  const createAccountMutation = useMutation(
    (request: CreateAccountRequest) => accountOnboardingApi.createAccount(request),
    {
      onSuccess: async (response: Account) => {
        queryClient.invalidateQueries(accountSummaryKeys.lists());
        queryClient.invalidateQueries(joinedAccountKeys.lists());
        queryClient.invalidateQueries(profileQueryKey());
      },
    }
  );

  return {
    createAccountMutation,
  };
};
